import {Box, HBox} from '@buncha/components/core/view';
import {CustomText, H6Text} from '@buncha/components/core/text';
import {
  OrderSubstitutionPreference,
  SubstitutionChoice,
} from '@buncha/itemDetails/types/itemDetails';
import React, {useMemo} from 'react';

import {ChevronRightIcon} from 'native-base';
import DeleteTrashEmpty from '@buncha/components/core/icon/DeleteTrashEmpty';
import OpenBagIcon from '@buncha/components/core/icon/OpenBagIcon';
import {StyleSheet} from 'react-native';
import {Touchable} from '@buncha/components/core/touchable';
import {useColorThemeType} from '@buncha/appStyles/hooks/theme';

interface Props {
  onPress: () => void;
  substitutionChoice: SubstitutionChoice;
  hasSubstitutionOptions: boolean;
  isCustomItem: boolean;
  substitutionPreference?: OrderSubstitutionPreference;
}

const SubstitutionInfoBanner = (props: Props) => {
  const theme = useColorThemeType();

  const {
    substitutionChoice,
    onPress,
    hasSubstitutionOptions,
    substitutionPreference,
    isCustomItem,
  } = props;

  const getSubTitle = useMemo(() => {
    if (hasSubstitutionOptions && !isCustomItem)
      return 'The customer has chosen a backup item.\nProceed to add the selected substitute.';
    if (
      isCustomItem ||
      (!hasSubstitutionOptions &&
        substitutionPreference === OrderSubstitutionPreference.NOT_ALLOWED &&
        substitutionChoice === SubstitutionChoice.none)
    )
      return 'Customer opted not to receive a replacement. Proceed to drop this item';
    switch (substitutionChoice) {
      case SubstitutionChoice.noSubstitution:
        return 'Customer opted not to receive a replacement. Proceed to drop this item';
      case SubstitutionChoice.meijerBrand:
        return 'Select replacement for customer from any meijer brand';
      case SubstitutionChoice.nationalBrand:
        return 'Select replacement for customer from any national brand';
      default:
        if (hasSubstitutionOptions)
          return 'The customer has chosen a backup item.\nProceed to add the selected substitute.';
        return 'The customer has requested a replacement suggestion. Proceed to choose an alternative';
    }
  }, [
    hasSubstitutionOptions,
    isCustomItem,
    substitutionChoice,
    substitutionPreference,
  ]);

  const getIcon = useMemo(() => {
    if (
      isCustomItem ||
      (!hasSubstitutionOptions &&
        substitutionPreference === OrderSubstitutionPreference.NOT_ALLOWED &&
        substitutionChoice === SubstitutionChoice.none) ||
      substitutionChoice === SubstitutionChoice.noSubstitution
    )
      return <DeleteTrashEmpty width={20} height={20} viewBox="0 1 15 15" />;
    return <OpenBagIcon width={20} height={20} viewBox="-1 -1 20 20" />;
  }, [
    hasSubstitutionOptions,
    isCustomItem,
    substitutionChoice,
    substitutionPreference,
  ]);

  return (
    <Touchable onPress={onPress}>
      <HBox
        style={[
          styles.container,
          {
            borderColor: theme?.background.red[100],
          },
        ]}>
        <HBox style={styles.parentContainer}>
          <Box
            style={[
              styles.iconContainer,
              {
                backgroundColor: theme?.background.red[200],
              },
            ]}>
            {getIcon}
          </Box>
          <Box style={styles.itemBox}>
            <H6Text
              style={[
                styles?.itemUnavailableText,
                {color: theme?.text.red[100]},
              ]}>
              Item Unavailable?
            </H6Text>
            <CustomText
              style={[styles.subtitle, {color: theme?.text.blue[300]}]}>
              {getSubTitle}
            </CustomText>
          </Box>
        </HBox>
        <ChevronRightIcon
          color={theme?.text.red[100]}
          width={24}
          height={24}
          viewBox="-3 -1 25 25"
        />
      </HBox>
    </Touchable>
  );
};

export default React.memo(SubstitutionInfoBanner);

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderLeftWidth: 5,
    borderRadius: 4,
    flex: 1,
    paddingRight: 10,
    paddingVertical: 10,
  },
  parentContainer: {alignItems: 'flex-start', flex: 1},
  iconContainer: {
    borderRadius: 20,
    marginTop: 5,
    marginHorizontal: 10,
    padding: 3,
  },
  itemBox: {
    flex: 1,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  subtitle: {
    textAlign: 'left',
  },
  itemUnavailableText: {
    lineHeight: 15,
  },
});
